import type { SeenMedplumClient } from '@seen/core/client'
import type { JsonObject } from '@seen/core/json'
import type { StagedSuggestion } from '@seen/core/models/compositionNote'
import { isVersionedReferenceString } from '@seen/core/references'
import { generateJSON, type JSONContent } from '@tiptap/core'
import { Fragment, type Node as PMNode } from '@tiptap/pm/model'
import type { Editor } from '@tiptap/react'
import { orderBy } from 'lodash-es'
import { AI_PROPOSALS_PLUGIN_KEY } from '#src/components/rich-text-editor/AIProposalsExtension.ts'
import {
  isSuggestionContentPresent,
  narrowPersistedOp,
} from '#src/components/rich-text-editor/aiSuggestionReconcile.ts'
import {
  collectStampedNodes,
  hasSuggestionStamp,
  markAcceptInsert,
  stampAcceptId,
  updateAttrsByStamp,
} from '#src/components/rich-text-editor/aiSuggestionStamp.ts'
import {
  orphanedConditionRefs,
  runUndoFlipCompensation,
} from '#src/components/rich-text-editor/conditionMentionSideEffects.ts'
import {
  type FillAcceptPlan,
  fillAcceptPlan,
} from '#src/components/rich-text-editor/fillReplacement.ts'
import { suggestionContentHtml } from '#src/components/rich-text-editor/suggestionContentExtensions.ts'
import {
  contractForNode,
  type ResolvedContract,
} from '#src/components/rich-text-editor/suggestionContracts.ts'
import {
  resolveOperation,
  validateSuggestionContent,
} from '#src/contexts/CollaborativeEditorRegistry.tsx'
import {
  reportAcceptSideEffectFailure,
  reportAcceptStampsMissing,
} from '#src/hooks/mutationObservability.ts'
import type { DroppedSuggestion } from '#src/hooks/useAISuggestionHydration.ts'
import type { SuggestionPersistence } from '#src/hooks/useSuggestionPersistence.ts'

/*
 * The generic accept pipeline (design §1.3). It executes the five-rule accept
 * transaction policy (§O.4) over a note editor's persisted staging:
 *
 *   R1 receipt-first  · the receipt is persisted BEFORE any side effect
 *   R2 idempotent      · each applyOnAccept create keys on the stamp identity
 *   R3 resumable       · the entry check reads the synchronous stamp / receipt
 *   R4 defined failure · a failed side effect degrades to a draft-flagged node
 *   R5 compensation    · per-family, keyed on the stamp (conversion PRs)
 *
 * Order is deliberate: doc mutation FIRST, side effects second. Most accepts
 * (plain content, choice selections) have no side effects — the insert is the
 * spine and applyOnAccept a post-pass over the few nodes that need one. A failed
 * FHIR write leaves the clinician's editorial decision in the note as a visible,
 * retryable draft-flagged node rather than bouncing the accept.
 *
 * Phase 1.3 registers NO family with an `applyOnAccept`, so the stamp/receipt/
 * side-effect machinery is inert and every accept reduces to today's behavior
 * (recheck → insert → status projection). Phase 1.4 registers the first family
 * and the machinery activates unchanged.
 */
export interface AcceptPipelineDeps {
  editor: Editor
  persistence: SuggestionPersistence
  medplum: SeenMedplumClient
  /*
   * The edited resource's reference (e.g. `Composition/{id}`), handed to a
   * family's applyOnAccept as `ctx.resourceRef` — the condition family resolves
   * its encounter from it for diagnosis linkage. Null when the editor has no
   * backing resource.
   */
  resourceRef: string | null
  /*
   * The note's participant (`Patient/{id}`), handed to a family's validators
   * and side effects as `ctx.patientRef` so a reference belonging to a
   * different patient's chart is refused before it becomes a clinical link.
   * Null when no participant is in scope, which skips ownership checks.
   */
  patientRef: string | null
  /* Route a recheck failure to the 1.1b dropped-list surface (contained: this suggestion only). */
  onDropped: (dropped: DroppedSuggestion) => void
  /* Injected for tests; defaults to the real contract registry. */
  resolveContract?: typeof contractForNode
  /* The ai-note-suggestions gate, threaded to every validateSuggestionContent /
   * resolveOperation / resolveContract call in this pipeline — see
   * mentionContractFor. */
  noteSuggestionsEnabled: boolean
  /* The ai-citation-mentions gate, threaded alongside noteSuggestionsEnabled to
   * the same calls — see mentionContractFor. */
  citationMentionsEnabled: boolean
}

/* Remove a suggestion's decoration (decoration-only; no status write). */
function dropDecoration(editor: Editor, id: string): void {
  editor
    .chain()
    .command(({ tr }) => {
      tr.setMeta(AI_PROPOSALS_PLUGIN_KEY, { remove: [id] })
      return true
    })
    .run()
}

/*
 * Revalidate island-edited content before it is (re)inserted (design §2.b). The
 * invariant-1 content gate must hold on EVERY insert path — the primary accept
 * AND the resume re-inserts — never just one, since accept-time validation
 * becomes the PRIMARY admission guard once Option C removes the sign gate. The
 * op content itself is already validated by resolveOperation, so absent
 * editedContent passes through. Returns true to proceed; on failure returns
 * false AND drops the suggestion to the dropped list, so no unvalidated content
 * is ever inserted.
 */
function revalidateEditedOrDrop(
  deps: AcceptPipelineDeps,
  record: StagedSuggestion,
  opType: string,
  editedContent: string | undefined
): boolean {
  if (editedContent === undefined) {
    return true
  }
  const error = validateSuggestionContent(
    deps.editor,
    opType,
    editedContent,
    deps.noteSuggestionsEnabled,
    deps.citationMentionsEnabled
  )
  if (!error) {
    return true
  }
  dropDecoration(deps.editor, record.id)
  deps.onDropped({
    id: record.id,
    label: record.label ?? 'Suggestion',
    reason: error.error,
  })
  return false
}

interface PendingAcceptValidator {
  attrs: JsonObject
  validate: NonNullable<ResolvedContract['validateOnAccept']>
}

/*
 * Collect the to-be-inserted nodes whose family declares a `validateOnAccept`
 * precondition (Option C, design State 1). SYNCHRONOUS by design: a plain-content
 * accept (no side-effect node) must stay fully synchronous through the insert so
 * its decoration-drop dedup holds — only an accept with ≥1 side-effect node takes
 * the async validation path. Walks the content that will ACTUALLY be inserted (the
 * island-edited override when present), so an edit that retargets which condition
 * a chip tags is what gets validated, not the stale AI op.
 */
function collectAcceptValidators(
  deps: AcceptPipelineDeps,
  html: string
): PendingAcceptValidator[] {
  const resolveContract = deps.resolveContract ?? contractForNode
  const json = generateJSON(
    html,
    deps.editor.extensionManager.extensions
  ) as JSONContent
  const pending: PendingAcceptValidator[] = []
  const visit = (node: JSONContent): void => {
    const attrs: JsonObject = node.attrs ?? {}
    const validate = node.type
      ? resolveContract(
          node.type,
          attrs,
          deps.noteSuggestionsEnabled,
          deps.citationMentionsEnabled
        )?.validateOnAccept
      : undefined
    if (validate) {
      pending.push({ attrs, validate })
    }
    node.content?.forEach(visit)
  }
  visit(json)
  return pending
}

/*
 * Run a suggestion's accept side effects (R2): for each stamped node whose
 * family declares `applyOnAccept`, run its conditional create keyed on the stamp
 * identity (active-only, idempotent — a crash-and-retry cannot duplicate a
 * resource) and patch the node's attrs by stamp. A failing `applyOnAccept`
 * leaves the node draft-flagged (R4): the create is not retried here, the node
 * stays visible and retryable from the receipt. Idempotent, so a resume re-runs
 * it without redoing completed effects.
 */
async function runSideEffects(
  deps: AcceptPipelineDeps,
  id: string,
  // `force` re-runs each side effect even for an already-VERSIONED node — the
  // redo-reapply path (reapplyAcceptSideEffects) needs it because a redo restores
  // the chip already carrying its versioned reference, yet the intervening undo
  // tore down the encounter link + receipt this must re-establish.
  opts?: { force?: boolean }
): Promise<void> {
  const { editor, persistence, medplum, resourceRef } = deps
  const resolveContract = deps.resolveContract ?? contractForNode
  const stampedNodes = collectStampedNodes(editor.state.doc, id)
  // The receipt names the stamps this accept inserted; finding NONE of them
  // means the content was removed while the receipt write was in flight, so
  // everything below silently does nothing and the record is left undecided.
  // Not recoverable by the reconcile sweep either — with no stamped node there
  // is nothing for its draft-flagged retry to find — so this report is the only
  // trace the accept happened at all.
  if (stampedNodes.length === 0) {
    const receipt = persistence.getReceipt(id)
    if (receipt && receipt.nodeIds.length > 0) {
      reportAcceptStampsMissing({ suggestionId: id, nodeIds: receipt.nodeIds })
    }
  }
  for (const { node, nodeId } of stampedNodes) {
    // Repair, not admission — every node here already carries an accept stamp,
    // and stampAcceptId only stamps what the gate admitted, so a stamped
    // condition node is proof the gate was ON when it was accepted. Resolving
    // against the CURRENT flag would strand it if the flag went off in between:
    // undo still unlinks Encounter.diagnosis (compensate is called directly, not
    // through the registry) while redo could not relink it, and the sweep could
    // never finalize a draft-flagged node.
    const contract = resolveContract(node.type.name, node.attrs, true, true)
    if (!contract?.applyOnAccept) {
      continue
    }
    // Already finalized (a resume over a completed node): its side effect wrote
    // the frozen VERSIONED reference, so skip — never redo. An unversioned
    // reference is the pre-accept / draft-flagged state (R4) whose side effect
    // must still run or retry — under the no-create model the node carries its
    // reference from the start, so presence alone is not "done"; versioning is.
    // `force` overrides the skip for the redo-reapply, whose restored chip is
    // versioned but whose FHIR side state an undo already removed.
    if (
      !opts?.force &&
      typeof node.attrs.reference === 'string' &&
      isVersionedReferenceString(node.attrs.reference)
    ) {
      continue
    }
    // R4 — a failed applyOnAccept leaves the node draft-flagged: visible,
    // editable, and retryable from the receipt (a later resume re-runs this).
    // The create is idempotent (keyed on the stamp), so the retry never
    // duplicates a resource. We do NOT rethrow — one node's failed side effect
    // must not abort the accept or a sibling node's side effect.
    let result: Awaited<ReturnType<NonNullable<typeof contract.applyOnAccept>>>
    try {
      result = await contract.applyOnAccept(node.attrs, {
        medplum,
        suggestionId: id,
        nodeId,
        resourceRef,
        patientRef: deps.patientRef,
      })
    } catch (error) {
      // Swallowed by design (R4), but never silently: without this the only
      // trace that a clinician's accept lost its durable half is the absent
      // FHIR write itself, which is undiagnosable after the fact.
      reportAcceptSideEffectFailure({
        suggestionId: id,
        nodeId,
        nodeType: node.type.name,
        error,
      })
      continue
    }
    if (result) {
      if (result.patch) {
        updateAttrsByStamp(editor, nodeId, result.patch)
      }
      // Receipt ownership: record ONLY what the apply says it created
      // (`createdRef`), never everything it patched — a pre-existing
      // Encounter.diagnosis link in createdRefs would make an undo of this
      // accept unlink a diagnosis the accept never added.
      if (typeof result.createdRef === 'string') {
        await persistence.recordCreatedRef(id, nodeId, result.createdRef)
      }
    }
  }
}

/*
 * Resume an interrupted accept (R3). Reached when the entry check finds an
 * in-doc stamp or a persisted receipt: a double-click landing in the
 * insert→receipt window, or a re-mount after a crash. Never re-inserts stamped
 * content that is still present and never redoes a completed side effect; it
 * re-materializes content the crash lost (receipt present, stamp gone — H6),
 * (re)records the receipt, and re-runs the idempotent side-effect post-pass.
 */
export async function resumeAccept(
  deps: AcceptPipelineDeps,
  id: string
): Promise<void> {
  const { editor, persistence } = deps
  const all = persistence.getAllSuggestions()
  const record = all.find((s) => s.id === id)
  const receipt = persistence.getReceipt(id)
  const stampGone = !hasSuggestionStamp(editor.state.doc, id)
  /* Sibling-aware (see `siblingInsertSpan`): an accept another suggestion at the
   * same insertion point pushed out of its own window is still applied, and
   * re-inserting it here is what duplicated clinical content in the note. */
  const contentAbsent =
    !!record &&
    isSuggestionContentPresent(editor, record.op, record, all) === false

  // The two re-insert branches below (H6 lost-insert; undo re-insert). When
  // either would re-materialize island-edited content, revalidate it FIRST —
  // the resume insert path must clear the SAME invariant-1 gate as the primary
  // accept (accept persists the validated content, so this normally passes; it
  // is the belt-and-suspenders that keeps NO insert path ungated). A failure
  // drops the suggestion rather than re-inserting unvalidated content.
  const h6Reinsert =
    !!record && !!receipt && receipt.nodeIds.length > 0 && stampGone
  const undoReinsert =
    !!record && !!receipt && receipt.nodeIds.length === 0 && contentAbsent
  if (record && (h6Reinsert || undoReinsert)) {
    const op = narrowPersistedOp(record.op)
    if (
      op &&
      !revalidateEditedOrDrop(deps, record, op.type, record.editedContent)
    ) {
      return
    }
  }

  // H6 — a crash persisted the receipt but lost the un-synced Yjs insert. Re-
  // insert the SAME stamped content (keyed to the receipt's node ids) so the
  // accepted content is not silently lost.
  if (record && h6Reinsert) {
    insertStampedContent(deps, record, undefined, record.editedContent)
  }

  // A text / no-side-effect accept (empty receipt) carries no stamp to key
  // content presence on, so an undo of its accept leaves the receipt behind and
  // the entry check routes re-accept here (R3). Re-insert ONLY when the content
  // is genuinely ABSENT — an undo removed it, and main re-inserts (undo →
  // re-accept restores content). NEVER when it is present (crash-and-retry) or
  // indeterminate (an op type isSuggestionContentPresent can't reconcile), so a
  // crash-and-retry cannot double-insert. The receipt is left intact: a receipt
  // is the R5 compensation key and is never silently cleared — an empty receipt
  // has no side effect to compensate, a non-empty one must survive.
  if (record && undoReinsert) {
    insertStampedContent(deps, record, undefined, record.editedContent)
  }

  // The stamp node ids this resume finalizes — the receipt's when present, else
  // the freshly-collected stamps (recorded now as the receipt). Passed to
  // projectAcceptedIfPresent so wasStamped derives from the actual stamps, not a
  // fresh getReceipt that can close over the pre-write cache (H2b).
  const nodeIds = receipt
    ? receipt.nodeIds
    : collectStampedNodes(editor.state.doc, id).map((n) => n.nodeId)
  // stamp without receipt (crash after insert, before the receipt write) —
  // record it now so a later resume has the durable signal.
  if (!receipt) {
    await persistence.recordAcceptReceipt(id, nodeIds)
  }

  await runSideEffects(deps, id)
  projectAcceptedIfPresent(deps, id, nodeIds, record?.editedContent)
}

/*
 * A stamped node still carrying an UNVERSIONED reference — the R4 draft-flagged
 * state whose applyOnAccept never finalized (a transient FHIR failure or a
 * precondition-race at accept time). Under the no-create model a versioned
 * reference is the completion marker, so an unversioned one is a retry
 * candidate. A missing / non-string reference is a reference-less mention with
 * nothing to link — not draft-flagged, so it is NOT a retry candidate.
 */
function hasUnversionedStampedNode(doc: PMNode, id: string): boolean {
  return collectStampedNodes(doc, id).some(({ node }) => {
    const reference = node.attrs?.reference
    return (
      typeof reference === 'string' && !isVersionedReferenceString(reference)
    )
  })
}

/*
 * Client-side reconcile sweep (design State 2): the self-healing backstop that
 * lets the accept path converge without the sign gate. For each receipt-bearing
 * suggestion it drives whichever of two residual states applies — both reuse the
 * verified accept/undo machinery, both idempotent, and neither touches a healthy
 * accepted suggestion:
 *
 *   1. ORPHAN UNLINK — a receipt whose created condition link is no longer
 *      referenced by any content node (an undo removed the chip but
 *      `runUndoFlipCompensation`'s swallowed-failure catch left the FHIR unlink
 *      stranded). Re-drives that same wiring: SIBLING-SAFE (orphanedConditionRefs
 *      keeps a condition another chip still tags) and RECEIPT-OWNED-ONLY (only the
 *      refs THIS receipt created). Gated on a non-empty orphan set, so a healthy
 *      accept — whose condition its own chip still references — is a no-op and its
 *      receipt refs are never cleared (clearing them would break a later undo's
 *      compensation).
 *   2. DRAFT-FLAGGED RETRY — a stamped node still carrying an unversioned
 *      reference (R4). Re-runs `resumeAccept`, already idempotent for this exact
 *      case: it re-fires the stamp-keyed side effect and versions the node.
 *
 * The two states are mutually exclusive in the doc (an orphan has no surviving
 * stamped node; a draft-flagged node's chip still references its condition), so
 * one direction runs per suggestion per pass. Idempotent: once every receipt is
 * converged (versioned node, no orphan) the sweep is a no-op, so a repeat pass
 * settles to nothing. Sequential by suggestion so concurrent encounter writes
 * don't race. Never throws — every failure is swallowed by the reused machinery —
 * so a pre-sign drain can await it without risk of blocking the sign.
 */
export async function runReconcileSweep(
  deps: AcceptPipelineDeps
): Promise<void> {
  const { editor, persistence, medplum, resourceRef } = deps
  const doc = editor.state.doc
  /* Belt-and-suspenders against the hydration race (the reconcile-sweep effect
   * already gates on isDocSynchronized): a doc with NO textual content is either
   * unhydrated (cold reload, before the collaborative doc syncs) or a genuinely
   * emptied note — in neither case should a committed accept's condition links be
   * read as "orphaned" and destructively unlinked. Skip the orphan-unlink when
   * the doc is empty; a real orphan (the chip removed while the note still has
   * content) is unaffected, and the draft-flagged retry below is a no-op on an
   * empty doc anyway.
   *
   * The pre-sign drain (SignButton) calls this sweep with NO isDocSynchronized
   * gate, so this check is its only unhydrated-doc guard. That is sound only
   * while an unsynced collaborative doc is always EMPTY — there is no local Yjs
   * persistence that could hydrate stale text before sync. If such persistence
   * is ever added, thread the sync signal into these deps and gate the sign-time
   * call too. */
  const docIsEmpty = doc.textContent.trim() === ''
  for (const record of persistence.getAllSuggestions()) {
    const id = record.id
    const receipt = persistence.getReceipt(id)
    if (!receipt) {
      continue
    }
    if (
      !docIsEmpty &&
      orphanedConditionRefs(receipt.createdRefs, doc).length > 0
    ) {
      await runUndoFlipCompensation({
        medplum,
        suggestionId: id,
        resourceRef,
        doc,
        receipt,
        clearReceiptRefs: persistence.clearReceiptRefs,
      })
      continue
    }
    if (hasUnversionedStampedNode(doc, id)) {
      await resumeAccept(deps, id)
    }
  }
}

/*
 * Accept a suggestion through the full pipeline. Idempotent and resumable:
 * a re-entry (double-click, retry) resumes rather than re-inserting or
 * re-firing side effects.
 */
export async function acceptSuggestion(
  deps: AcceptPipelineDeps,
  id: string,
  /*
   * Island-edited content (design §2.b), passed by Accept-with-edits straight
   * from the open island's controller — the pipeline inserts THIS instead of the
   * AI op content, revalidated through the same 1.1 gate. Omitted for a plain
   * accept, which falls back to the record's persisted `editedContent` (a
   * suggestion edited then closed without accepting), then the AI op content.
   */
  editedContentOverride?: string
): Promise<void> {
  const { editor, persistence } = deps

  // R3 entry — a persisted receipt or an in-doc stamp means an accept is already
  // in flight/done: resume. The stamp is the synchronous signal for a
  // side-effect (custom-node) accept in the insert→receipt window (R3 stamps
  // custom nodes only), so it wins even when the decoration is already gone.
  if (hasSuggestionStamp(editor.state.doc, id) || persistence.getReceipt(id)) {
    return resumeAccept(deps, id)
  }

  // Synchronous double-click dedup for a TEXT accept (which carries no stamp —
  // R3 stamps custom nodes only). The first click removes the decoration in the
  // same tick, so a second click in the insert→receipt window (before the async
  // receipt is visible) finds no proposal here and no-ops. This preserves main's
  // exact plain-content accept semantics, which the receipt/stamp resume above
  // covers only for side-effect accepts.
  const proposals = AI_PROPOSALS_PLUGIN_KEY.getState(editor.state)?.proposals
  if (!proposals?.has(id)) {
    return
  }

  // Q5 concurrency guard — already decided elsewhere (accepted/rejected): drop
  // the stale decoration without inserting duplicate clinical content.
  if (!persistence.guardAccept(id)) {
    dropDecoration(editor, id)
    return
  }

  const record = persistence.getAllSuggestions().find((s) => s.id === id)
  if (!record) {
    return
  }

  // Recheck — the full 1.1 gate + placement re-run on the current doc (content
  // may have drifted since it was painted). A failure is contained to THIS
  // suggestion: route it to the dropped list, never bounce the note session.
  const op = narrowPersistedOp(record.op)
  const resolved = op
    ? resolveOperation(
        editor,
        op,
        deps.noteSuggestionsEnabled,
        deps.citationMentionsEnabled
      )
    : null
  if (!op || !resolved || 'error' in resolved) {
    dropDecoration(editor, id)
    deps.onDropped({
      id,
      label: record.label ?? 'Suggestion',
      reason: resolved
        ? (resolved as { error: string }).error
        : 'This suggestion can no longer be applied to the note.',
    })
    return
  }

  // Revalidate-on-accept (design §2.b): the 1.1 content gate re-runs on the
  // island-edited HTML (override, or the persisted edit) — the edit, not the AI
  // op, is what actually gets inserted. A failure is contained to THIS suggestion.
  const editedContent = editedContentOverride ?? record.editedContent
  if (!revalidateEditedOrDrop(deps, record, op.type, editedContent)) {
    return
  }

  // Persist the accepted edit durably BEFORE the receipt (design §2.b): a
  // crash-resume re-materializes from record.editedContent, so it must equal
  // EXACTLY the validated content just accepted — not the last debounced
  // snapshot, which can trail the live override by up to the debounce window.
  // Only for an override (Accept-with-edits); a plain accept already sourced its
  // content from record.editedContent.
  if (editedContentOverride !== undefined) {
    await persistence.projectEditedContent(id, editedContentOverride)
  }

  // Accept-time validation backstop (Option C, design State 1): for content with
  // ≥1 side-effect node, refuse an accept whose to-be-inserted content tags a
  // condition that no longer exists / is inactive — the correctness backstop for
  // the reveal→click freshness gap (a condition can be resolved/inactivated
  // between a suggestion painting and the accept click). Validates the POST-island-
  // edit content (editedContent when present), never the stale AI op, since an
  // island edit can retarget the tagged condition; the HTML mirrors
  // insertStampedContent's own source. Positioned AFTER the editedContent
  // revalidation and BEFORE the insert so no unvalidated side effect is committed.
  // A plain-content accept has no validators and stays fully synchronous — its
  // decoration-drop dedup is unaffected.
  const contentToInsert =
    editedContent ??
    suggestionContentHtml(resolved.render, resolved.htmlContent)
  const validators = collectAcceptValidators(deps, contentToInsert)
  if (validators.length > 0) {
    for (const { attrs, validate } of validators) {
      const result = await validate(attrs, {
        medplum: deps.medplum,
        patientRef: deps.patientRef,
      })
      if (!result.ok) {
        // A retryable refusal means the precondition READ failed, not the
        // precondition: abort this accept but keep the suggestion painted and
        // pending, so the next click retries instead of a network hiccup
        // permanently dropping it with a false clinical reason.
        if (result.retryable) {
          return
        }
        dropDecoration(editor, id)
        deps.onDropped({
          id,
          label: record.label ?? 'Suggestion',
          reason: result.reason,
        })
        return
      }
    }

    // R3 re-entry after the validation await(s) (design State 1 double-insert
    // safety): the awaits above (and projectEditedContent's) open a window a rapid
    // double-click can land in. A side-effect node always carries a stamp, so if a
    // concurrent accept already inserted+stamped this suggestion, resume instead of
    // inserting a second copy — the SAME entry check the top of the pipeline runs.
    // insertStampedContent below is synchronous, so whichever continuation reaches
    // it first stamps the doc before yielding.
    if (
      hasSuggestionStamp(editor.state.doc, id) ||
      persistence.getReceipt(id)
    ) {
      return resumeAccept(deps, id)
    }
  }

  // Re-resolve the placement against the CURRENT doc. `resolved` above was
  // computed before projectEditedContent and validateOnAccept, both of which
  // yield; a concurrent peer edit or a local undo landing in that window shifts
  // every numeric position after it, so inserting at the pre-await target drops
  // clinical content into the wrong region while still stamping it and linking
  // Encounter.diagnosis. The placement must be derived from the doc the insert
  // actually mutates, not the one the accept was decided against.
  const placement = resolveOperation(
    editor,
    op,
    deps.noteSuggestionsEnabled,
    deps.citationMentionsEnabled
  )
  if ('error' in placement) {
    dropDecoration(editor, id)
    deps.onDropped({
      id,
      label: record.label ?? 'Suggestion',
      reason: placement.error,
    })
    return
  }
  // The proposal guard, repeated for the same reason: an undo/settle in the
  // await window can affirmatively drop this proposal, and re-adding its content
  // afterwards would resurrect a suggestion the document no longer carries.
  if (!AI_PROPOSALS_PLUGIN_KEY.getState(editor.state)?.proposals.has(id)) {
    return
  }

  // Doc mutation FIRST — insert the (stamped) content and drop the decoration in
  // one transaction (grouped into one undo step where the UndoManager allows).
  const nodeIds = insertStampedContent(deps, record, placement, editedContent)

  // R1 — receipt persisted BEFORE any side effect, awaited so the ordering holds.
  await persistence.recordAcceptReceipt(id, nodeIds)

  // R2/R4 — side effects second, keyed on the stamp; a failure degrades to a
  // draft-flagged node rather than bouncing the accept.
  await runSideEffects(deps, id)

  projectAcceptedIfPresent(deps, id, nodeIds, editedContent)
}

/*
 * Re-apply an accept's SIDE EFFECTS after a redo re-inserted its chip (the
 * redo-flip counterpart to runUndoFlipCompensation). On Cmd+Shift+Z the content
 * reconciler restores the stamped chip — carrying its already-VERSIONED
 * reference — but the intervening undo tore down the FHIR half (encounter
 * unlinked, receipt cleared). This re-links the encounter (idempotent
 * link-if-absent) and restores the receipt's createdRefs, WITHOUT re-inserting
 * content (the chip is already back) and WITHOUT the versioned-skip (force). No
 * stamped node → nothing was restored → no-op. Idempotent: a repeat is a
 * link-if-absent no-op, so it never double-links Encounter.diagnosis.
 */
export async function reapplyAcceptSideEffects(
  deps: AcceptPipelineDeps,
  id: string
): Promise<void> {
  const { editor, persistence } = deps
  const nodeIds = collectStampedNodes(editor.state.doc, id).map((n) => n.nodeId)
  if (nodeIds.length === 0) {
    return
  }
  // The undo left the receipt shell (its nodeIds) in place and only emptied
  // createdRefs, so this is normally a no-op; it re-seeds the shell should a
  // reload have dropped it, so recordCreatedRef below has a receipt to write to.
  await persistence.recordAcceptReceipt(id, nodeIds)
  await runSideEffects(deps, id, { force: true })
  projectAcceptedIfPresent(deps, id, nodeIds)
}

/*
 * Insert a suggestion's resolved content, stamped (R3), and drop its decoration
 * — one transaction. Returns the stamp node ids for the receipt. Re-resolves the
 * op when a resolved spec isn't supplied (the H6 resume re-insert). When
 * `editedContent` is present (design §2.b), it is inserted in place of the AI op
 * content — it is already the island's full, wrapped HTML — while the op still
 * supplies the placement (from/to/kind). Either way the HTML is parsed by
 * stampAcceptId through the preview's parse, never handed to insertContentAt as
 * a raw string, so the note gets the blocks the preview showed.
 */
function insertStampedContent(
  deps: AcceptPipelineDeps,
  record: StagedSuggestion,
  resolvedSpec?: Exclude<
    ReturnType<typeof resolveOperation>,
    { error: string }
  >,
  editedContent?: string
): string[] {
  const { editor } = deps
  const resolveContract = deps.resolveContract ?? contractForNode
  const op = narrowPersistedOp(record.op)
  const resolved =
    resolvedSpec ??
    (op
      ? resolveOperation(
          editor,
          op,
          deps.noteSuggestionsEnabled,
          deps.citationMentionsEnabled
        )
      : null)
  if (!resolved || 'error' in resolved) {
    return []
  }
  const html =
    editedContent ??
    suggestionContentHtml(resolved.render, resolved.htmlContent)
  /* A fill-only replace that keeps some of the text it replaces leaves that
   * text exactly as stored — marks included, however the model restated it —
   * and only removes what it drops and inserts what it adds, as the preview
   * showed. */
  const plan =
    resolved.kind === 'replace' && resolved.fillOnly
      ? fillAcceptPlan(editor.state.doc, resolved.from, resolved.to, html)
      : null
  if (plan) {
    return applyFillAcceptPlan(deps, record.id, resolved.to, plan)
  }
  const stamped = stampAcceptId(
    editor,
    html,
    record.id,
    deps.noteSuggestionsEnabled,
    deps.citationMentionsEnabled,
    resolveContract
  )
  const target =
    resolved.kind === 'replace'
      ? { from: resolved.from, to: resolved.to }
      : resolved.from
  // Content that parsed to nothing goes in as the empty STRING, never as `[]`:
  // insertContentAt routes an empty array to schema.nodeFromJSON, which throws,
  // and the note editor's enableContentCheck turns that throw into a
  // contentError — which disables collaboration and reloads the page. The empty
  // string clears a replace range just as it did before the content was parsed
  // here, so an op that deletes text still deletes it.
  const content = stamped.content.length > 0 ? stamped.content : ''
  // Under the fill-only rule only, so an accept with the flags off is unchanged.
  const inline =
    resolved.kind === 'replace' && resolved.fillOnly
      ? inlineReplacement(editor.state.doc, resolved.from, resolved.to, content)
      : null
  const chain = editor.chain().focus()
  const placed = inline
    ? chain.command(({ tr }) => {
        // As nodes, so the text takes none of the marks around it.
        tr.replaceWith(
          resolved.from,
          resolved.to,
          Fragment.fromArray(
            inline.map((node) => editor.schema.nodeFromJSON(node))
          )
        )
        return true
      })
    : chain.insertContentAt(target, content)
  placed
    .command(({ tr }) => {
      tr.setMeta(AI_PROPOSALS_PLUGIN_KEY, { remove: [record.id] })
      // The note host queues a content save off this tag. Without it an "Accept
      // all" persists only its first accept: `focus` above early-returns once
      // the view is already focused, so accepts two onward fire no focus event
      // and nothing else in the host reacts to their doc change.
      markAcceptInsert(tr)
      return true
    })
    .run()
  return stamped.nodeIds
}

/* A replace inside one line that leaves the rest of that line in place
 * continues the line: one paragraph of replacement goes in as its inline
 * content, never splitting the line around a paragraph of its own. Null when
 * the range spans blocks or covers its whole block, or the replacement is not
 * one paragraph. */
function inlineReplacement(
  doc: PMNode,
  from: number,
  to: number,
  content: JSONContent[] | ''
): JSONContent[] | null {
  const [only, ...rest] = content || []
  const $from = doc.resolve(from)
  if (
    only?.type !== 'paragraph' ||
    rest.length > 0 ||
    !$from.sameParent(doc.resolve(to)) ||
    !$from.parent.isTextblock ||
    (from === $from.start() && to === $from.end())
  ) {
    return null
  }
  return only.content ?? []
}

/* Removes the runs a fill-only replace drops and inserts its addition, stamped,
 * where the last kept word now ends — one transaction, like any accept. A
 * space goes in front of the addition when the replacement had one there and
 * the document does not already end the kept text with one. */
function applyFillAcceptPlan(
  deps: AcceptPipelineDeps,
  id: string,
  rangeEnd: number,
  plan: FillAcceptPlan
): string[] {
  const { editor } = deps
  const stamped = plan.additionHtml
    ? stampAcceptId(
        editor,
        plan.additionHtml,
        id,
        deps.noteSuggestionsEnabled,
        deps.citationMentionsEnabled,
        deps.resolveContract ?? contractForNode
      )
    : undefined
  const { doc } = editor.state
  const isSpace = (pos: number): boolean =>
    pos >= 0 && /\s/.test(doc.textBetween(pos, pos + 1, ' ', ' '))
  /* The whitespace the range ends on once its runs are gone joins the kept
     text to the addition, and collapses to its first character: a label's
     space plus a placeholder's trailing one would read as a doubled gap. */
  const lineStart = doc.resolve(rangeEnd).start()
  const inRun = (pos: number): boolean =>
    plan.dropped.some((run) => pos >= run.from && pos < run.to)
  const gap: number[] = []
  for (let pos = rangeEnd - 1; pos >= lineStart; pos--) {
    if (inRun(pos)) {
      continue
    }
    if (!isSpace(pos)) {
      break
    }
    gap.push(pos)
  }
  const dropped = orderBy(
    [
      ...plan.dropped,
      ...gap.slice(0, -1).map((pos) => ({ from: pos, to: pos + 1 })),
    ],
    ['from'],
    ['desc']
  )
  // Every dropped run lies inside the range, so the end moves back by them all.
  const insertAt =
    rangeEnd - dropped.reduce((total, run) => total + run.to - run.from, 0)
  // The last character the kept text ends on once the runs are gone.
  let before = rangeEnd - 1
  for (const run of dropped) {
    if (before >= run.from && before < run.to) {
      before = run.from - 1
    }
  }
  /* An addition that opens with a block other than a paragraph (a list, a
     heading) is a block of its own: it goes in after the kept line's block,
     with no joining space, rather than splitting that line around it. */
  const [first] = stamped?.content ?? []
  const ownBlock = first !== undefined && first.type !== 'paragraph'
  const blockAt = ownBlock
    ? doc.resolve(rangeEnd).after() - (rangeEnd - insertAt)
    : undefined
  const spaced = !ownBlock && plan.spaced && before >= 0 && !isSpace(before)
  /* One paragraph of addition continues the line the kept text ends on, so it
     goes in as that paragraph's inline content rather than as a block of its
     own; several paragraphs stay blocks. */
  const [only, ...rest] = stamped?.content ?? []
  const content =
    only?.type === 'paragraph' && rest.length === 0
      ? only.content
      : stamped?.content.length
        ? stamped.content
        : undefined
  /* Inline content goes in as nodes rather than through insertContentAt,
     which types plain text with the marks at the insertion point — the bold of
     the label it follows. */
  const inline = only?.type === 'paragraph' && rest.length === 0
  editor
    .chain()
    .focus()
    .command(({ tr }) => {
      for (const run of dropped) {
        tr.delete(run.from, run.to)
      }
      const at = spaced ? insertAt + 1 : insertAt
      if (content && spaced) {
        tr.insert(insertAt, editor.schema.text(' '))
      }
      if (content && inline) {
        tr.insert(
          at,
          Fragment.fromArray(
            content.map((node) => editor.schema.nodeFromJSON(node))
          )
        )
      }
      return true
    })
    .insertContentAt(
      blockAt ?? (spaced ? insertAt + 1 : insertAt),
      content && !inline ? content : ''
    )
    .command(({ tr }) => {
      tr.setMeta(AI_PROPOSALS_PLUGIN_KEY, { remove: [id] })
      // See `insertStampedContent`: the host saves off this tag.
      markAcceptInsert(tr)
      return true
    })
    .run()
  return stamped?.nodeIds ?? []
}

/*
 * Project the accepted status (design §O.4 R4, content-guarded — H2b). This runs
 * several awaits after the insert, so an undo can land in that window; projecting
 * unconditionally would then write `accepted` for content the document no longer
 * holds. Both accept shapes are checked against the CURRENT doc:
 *
 *   - stamped accept — the stamp must still be present (the original H2b guard).
 *   - unstamped (plain-text) accept — no stamp to key on, so ask the same
 *     content-presence question the reconciler asks. Only a definite `false`
 *     blocks: indeterminate (`null`) is not evidence of an undo, exactly as the
 *     reconciler treats it, so an op type presence can't reconcile still projects.
 *
 * This is a backstop, not the fix for the undo-during-accept race — the
 * reconciler's own flip is already queued ahead of this on the same serialized
 * write chain and settles the status. `nodeIds` is the ids insertStampedContent
 * actually produced (or the receipt's, on resume) — NOT a fresh getReceipt read,
 * which can close over the pre-write cache and mis-derive wasStamped (H2b).
 */
function projectAcceptedIfPresent(
  deps: AcceptPipelineDeps,
  id: string,
  nodeIds: string[],
  /*
   * The content this accept actually inserted, when an island edit stood in for
   * the AI op content. Passed explicitly rather than re-read off the record:
   * projectEditedContent's write is async and not synchronously visible through
   * getAllSuggestions, so the record still carries the PRE-edit value here and
   * the presence check would look for content the accept never inserted.
   */
  editedContent?: string
): void {
  const { editor, persistence } = deps
  const wasStamped = nodeIds.length > 0
  if (wasStamped) {
    if (!hasSuggestionStamp(editor.state.doc, id)) {
      return
    }
  } else {
    const all = persistence.getAllSuggestions()
    const record = all.find((s) => s.id === id)
    if (
      record &&
      isSuggestionContentPresent(
        editor,
        record.op,
        { id, editedContent: editedContent ?? record.editedContent },
        all
      ) === false
    ) {
      return
    }
  }
  persistence.projectStatuses([{ id, status: 'accepted' }])
}
